import type { SortingState } from '@tanstack/react-table';

import type { ReportDateRangeValue } from './api/fetchReportData';
import type { ReportViewConfig } from './api/reportViewTypes';
import {
    DEFAULT_REPORT_LIMIT,
    DEFAULT_REPORT_PAGE,
    type ReportPageLimit,
    type ReportQueryParams,
} from './reportQueryParams';
import { getReportTableConfig } from './reportTableConfigs';

export function buildReportViewConfig(input: {
    columnIds: string[];
    dateRange: ReportDateRangeValue;
    sort: SortingState;
    search: string;
    limit: ReportPageLimit;
    ticketIds?: number[];
}): ReportViewConfig {
    return {
        columnIds: input.columnIds,
        dateRange: {
            range: input.dateRange.range,
            customStart: input.dateRange.customStart ?? null,
            customEnd: input.dateRange.customEnd ?? null,
        },
        sort: input.sort.map((item) => ({ id: item.id, desc: item.desc })),
        search: input.search,
        limit: input.limit,
        ...(input.ticketIds !== undefined ? { ticketIds: input.ticketIds } : {}),
    };
}

export function applyReportViewConfigToParams(
    baseReportId: string,
    viewId: number,
    config: ReportViewConfig,
): Partial<ReportQueryParams> {
    const dateRange = config.dateRange;
    const range = dateRange?.range ?? 'all-time';

    return {
        report: baseReportId,
        view: viewId,
        range,
        customStart:
            range === 'custom' && dateRange?.customStart ? dateRange.customStart : undefined,
        customEnd: range === 'custom' && dateRange?.customEnd ? dateRange.customEnd : undefined,
        q: config.search ?? '',
        sort: config.sort ?? [],
        page: DEFAULT_REPORT_PAGE,
        limit: config.limit ?? DEFAULT_REPORT_LIMIT,
        tickets: config.ticketIds,
    };
}

export function getValidColumnIdsForReport(
    reportId: string,
    columnIds: string[] | undefined,
): string[] {
    const tableConfig = getReportTableConfig(reportId);
    const validIds = new Set(tableConfig?.columns.map((column) => column.id) ?? []);
    const defaults = tableConfig?.defaultSelectedColumnIds ?? [];

    if (!columnIds || columnIds.length === 0) {
        return defaults;
    }

    const filtered = columnIds.filter((id) => validIds.has(id));
    return filtered.length > 0 ? filtered : defaults;
}

/** Normalize optional fields so saved configs compare cleanly for dirty-state checks. */
export function areReportViewConfigsEqual(
    left: ReportViewConfig,
    right: ReportViewConfig,
): boolean {
    const normalize = (config: ReportViewConfig) =>
        JSON.stringify({
            columnIds: config.columnIds ?? [],
            dateRange: {
                range: config.dateRange?.range ?? 'all-time',
                customStart: config.dateRange?.customStart ?? null,
                customEnd: config.dateRange?.customEnd ?? null,
            },
            sort: (config.sort ?? []).map((item) => ({ id: item.id, desc: item.desc })),
            search: config.search ?? '',
            limit: config.limit ?? DEFAULT_REPORT_LIMIT,
            ticketIds:
                config.ticketIds === undefined ? null : [...config.ticketIds].sort((a, b) => a - b),
        });

    return normalize(left) === normalize(right);
}
